import React, { useEffect, useState } from 'react';
import {
    Box,
    Typography,
    Button,
    IconButton,
    CircularProgress,
    Alert,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Avatar,
    Chip,
    TablePagination,
    Menu,
    MenuItem,
    ListItemIcon as MuiListItemIcon,
    Breadcrumbs,
    Link,
    TextField,
    InputAdornment,
} from '@mui/material';
import { Add, Refresh, Edit, Delete, Visibility, VisibilityOff, MoreVert, TableChart, Search, Info, Download, Upload } from '@mui/icons-material';
import { getProductsBySubCategory, deleteProduct, downloadProductsTemplate, uploadProductsExcel } from '../../services/Api';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import DeleteConfirm from '../../modals/DeleteConfirm';

const GetAllProducts = () => {
    const truncateWords = (text, count = 5) => {
        if (!text) return '-';
        // Remove HTML tags and replace HTML entities like &nbsp;
        const cleanText = text
            .replace(/<[^>]*>/g, ' ')
            .replace(/&nbsp;/g, ' ')
            .replace(/&amp;/g, '&')
            .replace(/&lt;/g, '<')
            .replace(/&gt;/g, '>')
            .replace(/\s+/g, ' ')
            .trim();
            
        const words = cleanText.split(/\s+/).filter(w => w.length > 0);
        if (words.length <= count) return cleanText;
        return words.slice(0, count).join(' ') + '...';
    };

    const { subCategoryId } = useParams();
    const location = useLocation();
    const navigate = useNavigate();

    const subCategoryName = location.state?.subCategoryName || 'Subcategory';
    const categoryName = location.state?.categoryName || 'Category';
    const categoryId = location.state?.categoryId;

    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [downloading, setDownloading] = useState(false);
    const [uploading, setUploading] = useState(false);

    const handleDownload = async () => {
        try {
            setDownloading(true);
            const response = await downloadProductsTemplate();
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'Products_Template.xlsx');
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
        } catch (err) {
            console.error(err);
            setError(err.response?.data?.message || err.message || 'Failed to download template');
        } finally {
            setDownloading(false);
        }
    };

    const handleUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        try {
            setUploading(true);
            setError(null);
            const formData = new FormData();
            formData.append('file', file);
            
            const response = await uploadProductsExcel(formData);
            if (response.success || response.message) {
                fetchProducts(); 
                setSuccess(response.message || 'Excel uploaded successfully.');
                setTimeout(() => setSuccess(null), 5000); 
            } else {
                setError(response?.message || 'Failed to upload excel file');
            }
        } catch (err) {
            console.error(err);
            setError(err.response?.data?.message || 'Error occurred during upload');
        } finally {
            setUploading(false);
            event.target.value = null; 
        }
    };

    // Pagination and Search state
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalRows, setTotalRows] = useState(0);
    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');

    // Menu state
    const [anchorEl, setAnchorEl] = useState(null);
    const [menuSelectedProduct, setMenuSelectedProduct] = useState(null);

    // Delete state
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState(null);

    // Debounce effect: update debouncedSearch after 200ms
    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearch(searchQuery);
            setPage(0);
        }, 200);

        return () => clearTimeout(handler);
    }, [searchQuery]);

    const fetchProducts = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await getProductsBySubCategory(subCategoryId, page + 1, rowsPerPage, debouncedSearch);
            if (response.success) {
                setProducts(response.data || []);
                setTotalRows(response?.totalRecords || response?.totalItems || response?.totalCount || response?.totalDocs || response?.total || response?.data?.totalRecords || response?.data?.totalItems || response?.data?.totalCount || response?.data?.totalDocs || response?.data?.total || response?.data?.totalElements || response?.data?.length || response.total || response.data?.length || 0);
            } else {
                setError(response?.message || 'Failed to fetch products');
            }
        } catch (err) {
            setError(err.response?.data?.message || err.message || 'An error occurred while fetching products');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, rowsPerPage, subCategoryId, debouncedSearch]);

    const handleMenuOpen = (event, product) => {
        setAnchorEl(event.currentTarget);
        setMenuSelectedProduct(product);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        setMenuSelectedProduct(null);
    };

    const handleDeleteClick = () => {
        setSelectedProduct(menuSelectedProduct);
        setDeleteDialogOpen(true);
        handleMenuClose();
    };

    const handleConfirmDelete = async () => {
        try {
            setLoading(true);
            const response = await deleteProduct(selectedProduct.id);
            if (response.success) {
                setDeleteDialogOpen(false);
                fetchProducts();
            } else {
                console.error('Failed to delete product');
            }
        } catch (err) {
            console.error('An error occurred while deleting the product:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    return (
        <Box>
            {/* Header Section */}
            <Box sx={{ mb: 4 }}>
                <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 1 }}>
                    <Link
                        underline="hover"
                        color="inherit"
                        href="#"
                        onClick={(e) => {
                            e.preventDefault();
                            navigate('/categories');
                        }}
                    >
                        Categories
                    </Link>
                    <Link
                        underline="hover"
                        color="inherit"
                        href="#"
                        onClick={(e) => {
                            e.preventDefault();
                            if (categoryId) {
                                navigate(`/categories/${categoryId}/items`, { state: { categoryName } });
                            } else {
                                navigate('/categories');
                            }
                        }}
                        sx={{ display: 'flex', alignItems: 'center' }}
                    >
                        {categoryName} Subcategories
                    </Link>
                    <Typography color="text.primary">{subCategoryName} Products</Typography>
                </Breadcrumbs>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
                    <Box>
                        <Typography variant="h4" sx={{ fontWeight: 300 }}>
                            {subCategoryName} Products
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Manage inventory for {subCategoryName} in {categoryName}
                        </Typography>
                    </Box>
                    <Button
                        variant="contained"
                        startIcon={<Add />}
                        onClick={() => navigate(`/products/modify/${subCategoryId}`, { state: { subCategoryName, categoryName, categoryId } })}
                        sx={{ px: 4, backgroundColor: 'primary.main', whiteSpace: 'nowrap', borderRadius: 2 }}
                    >
                        Create Product
                    </Button>
                </Box>

                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', justifyContent: 'flex-end', flexWrap: 'wrap', mt: 3 }}>
                    <Button 
                        variant="outlined" 
                        color="primary" 
                        startIcon={downloading ? <CircularProgress size={20} color="inherit" /> : <Download />}
                        onClick={handleDownload}
                        disabled={downloading}
                        sx={{ borderRadius: 2, whiteSpace: 'nowrap' }}
                    >
                        Download Template
                    </Button>
                    <Button 
                        variant="contained" 
                        color="warning" 
                        component="label"
                        startIcon={uploading ? <CircularProgress size={20} color="inherit" /> : <Upload />}
                        disabled={uploading}
                        sx={{ borderRadius: 2, whiteSpace: 'nowrap' }}
                    >
                        Upload Excel
                        <input 
                            type="file" 
                            hidden 
                            accept=".xlsx, .xls"
                            onChange={handleUpload} 
                        />
                    </Button>

                    <TextField
                        placeholder="Search product..."
                        size="small"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        sx={{
                            width: { xs: '100%', sm: 250 },
                            '& .MuiOutlinedInput-root': {
                                borderRadius: '20px',
                                backgroundColor: 'rgba(0,0,0,0.02)',
                            }
                        }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search sx={{ color: 'text.secondary', fontSize: 20 }} />
                                </InputAdornment>
                            ),
                        }}
                    />
                    <IconButton onClick={fetchProducts} disabled={loading} sx={{ color: 'primary.main', border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                        <Refresh />
                    </IconButton>
                </Box>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 4 }}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mb: 4 }}>{success}</Alert>}

            <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'text.border', borderRadius: 2, overflow: 'hidden' }}>
                <TableContainer>
                    <Table sx={{ minWidth: 800 }} aria-label="products table">
                        <TableHead sx={{ backgroundColor: 'rgba(0,0,0,0.02)' }}>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 700 }}>Image</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Product Name</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Information</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Highlights</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 700 }}>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading && products.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={7} align="center" sx={{ py: 10 }}>
                                        <CircularProgress />
                                    </TableCell>
                                </TableRow>
                            ) : products.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={7} align="center" sx={{ py: 10 }}>
                                        <Typography color="text.secondary">
                                            {debouncedSearch ? `No products matching "${debouncedSearch}"` : "No products found for this subcategory"}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                products.map((product) => (
                                    <TableRow key={product.id} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                        <TableCell>
                                            <Avatar
                                                variant="rounded"
                                                src={product.image || product.productImages?.[0]}
                                                sx={{ width: 50, height: 50, border: '1px solid', borderColor: 'text.border' }}
                                            >
                                                <TableChart />
                                            </Avatar>
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 500 }}>
                                            {product.name}
                                        </TableCell>
                                        <TableCell sx={{ maxWidth: 200 }}>
                                            <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                {truncateWords(product.information)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell sx={{ maxWidth: 200 }}>
                                            <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                {truncateWords(product.highlights)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                icon={product.isActive ? <Visibility /> : <VisibilityOff />}
                                                label={product.isActive ? "Active" : "Inactive"}
                                                size="small"
                                                sx={{
                                                    backgroundColor: product.isActive ? 'primary.main' : 'grey.400',
                                                    color: 'white',
                                                    fontWeight: 600,
                                                    '& .MuiChip-icon': { color: 'white', fontSize: 16 }
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell align="right">
                                            <IconButton onClick={(e) => handleMenuOpen(e, product)}>
                                                <MoreVert />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    component="div"
                    count={totalRows}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </Paper>

            {/* Actions Menu */}
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                elevation={1}
                PaperProps={{
                    sx: {
                        minWidth: 150,
                        border: '1px solid',
                        borderColor: 'text.border',
                        mt: 1,
                    }
                }}
            >
                <MenuItem onClick={() => {
                    navigate(`/sub-categories/${subCategoryId}/products/${menuSelectedProduct.id}/details`, { 
                        state: { 
                            product: menuSelectedProduct,
                            subCategoryName,
                            categoryName,
                            categoryId
                        } 
                    });
                    handleMenuClose();
                }}>
                    <MuiListItemIcon><Info fontSize="small" color="info" /></MuiListItemIcon>
                    Show Details
                </MenuItem>
                {/* <MenuItem onClick={() => {
                    navigate(`/sub-categories/${subCategoryId}/products/${menuSelectedProduct.id}/variants`, { 
                        state: { 
                            productName: menuSelectedProduct.name,
                            subCategoryName,
                            categoryName,
                            categoryId
                        } 
                    });
                    handleMenuClose();
                }}>
                    <MuiListItemIcon><Search fontSize="small" color="secondary" /></MuiListItemIcon>
                    Show Variants
                </MenuItem> */}
                <MenuItem onClick={() => {
                    navigate(`/products/modify/${subCategoryId}/${menuSelectedProduct.id}`, { 
                        state: { 
                            product: menuSelectedProduct,
                            subCategoryName,
                            categoryName,
                            categoryId
                        } 
                    });
                    handleMenuClose();
                }}>
                    <MuiListItemIcon><Edit fontSize="small" color="primary" /></MuiListItemIcon>
                    Edit Details
                </MenuItem>
                <MenuItem onClick={handleDeleteClick}>
                    <MuiListItemIcon><Delete fontSize="small" color="error" /></MuiListItemIcon>
                    Delete
                </MenuItem>
            </Menu>

            <DeleteConfirm
                open={deleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
                onConfirm={handleConfirmDelete}
                title="Delete Product?"
                message={`Are you sure you want to delete "${selectedProduct?.name}"? This action cannot be undone.`}
            />
        </Box>
    );
};

export default GetAllProducts;
